/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useMemo } from 'react';
import {
  Modal,
  Stack,
  TextInput,
  Button,
  Table,
  Checkbox,
  Text,
  LoadingOverlay,
  Badge,
  Group,
  Alert,
  Tabs,
  Avatar,
  Radio,
  Divider,
  Paper,
  Title,
  ActionIcon
} from '@mantine/core';
import { IconSearch, IconCheck, IconX, IconInfoCircle, IconUsers } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';
import httpClient from 'services/httpClient';

interface Module {
  moduleId: string;
  moduleName: string;
  courseCode?: string;
  description?: string;
  programId?: string;
  programName?: string;
  beelajarId?: number | null;
}

interface CourseParticipant {
  userId: number;
  fullname: string;
  email: string;
}

interface SelectedParticipant {
  binusianId: string;
  name: string;
  email: string;
  listModule: string[];
}

interface ModuleSearchModalProps {
  opened: boolean;
  onClose: () => void;
  onModulesSelected: (modules: Module[], mode?: 'add' | 'replace') => void;
  onParticipantsSelected?: (participants: SelectedParticipant[]) => void;
  selectedModuleIds?: string[];
  title?: string;
  programId?: string;
  statusKurikulum?: boolean;
  programModulesData?: Module[];
}

type ParticipantSelectionMethod = 'attendance-qr' | 'enrollment' | 'completion' | 'certificate';

const ModuleSearchModal: React.FC<ModuleSearchModalProps> = ({
  opened,
  onClose,
  onModulesSelected,
  onParticipantsSelected,
  selectedModuleIds = [],
  title = 'Search & Select Modules',
  programId,
  statusKurikulum = false,
  programModulesData = []
}) => {
  // Module selection state
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [debouncedModuleSearch] = useDebouncedValue(searchInput, 300);

  // Participant selection state
  const [activeTab, setActiveTab] = useState<'modules' | 'participants'>('modules');
  const [participantMethod, setParticipantMethod] =
    useState<ParticipantSelectionMethod>('completion');
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>([]);
  const [participantModuleAssignments, setParticipantModuleAssignments] = useState<
    Record<string, string[]>
  >({});
  const [participantSearchInput, setParticipantSearchInput] = useState('');
  const [participantPage, setParticipantPage] = useState(1);
  const [debouncedParticipantSearch] = useDebouncedValue(participantSearchInput, 300);

  const [selectedAttendanceQR, setSelectedAttendanceQR] = useState<string>('');
  const [attendanceQRList, setAttendanceQRList] = useState<any[]>([]);
  const [isLoadingAttendanceQR, setIsLoadingAttendanceQR] = useState(false);
  const [attendanceQRPage, setAttendanceQRPage] = useState(1);
  const [attendanceQRTotalPages, setAttendanceQRTotalPages] = useState(0);

  const [selectedCertificate, setSelectedCertificate] = useState<string>('');
  const [certificateList, setCertificateList] = useState<any[]>([]);
  const [isLoadingCertificates, setIsLoadingCertificates] = useState(false);
  const [certificatePage, setCertificatePage] = useState(1);
  const [certificateTotalPages, setCertificateTotalPages] = useState(0);

  // Enrollment selection state
  const [selectedEnrollmentYear, setSelectedEnrollmentYear] = useState<string>('');
  const [enrollmentYearPage, setEnrollmentYearPage] = useState(1);

  useEffect(() => {
    if (opened) {
      setSelectedModules([]);
      setSearchInput('');
      setSearchTerm('');
      setPage(1);
      setActiveTab('modules');
      setSelectedParticipants([]);
      setParticipantModuleAssignments({});
      setParticipantSearchInput('');
      setParticipantPage(1);
      setSelectedAttendanceQR('');
      setAttendanceQRPage(1);
      setSelectedCertificate('');
      setCertificatePage(1);
      setSelectedEnrollmentYear('');
      setEnrollmentYearPage(1);
    }
  }, [opened]);

  useEffect(() => {
    if (statusKurikulum) {
      setSearchTerm(debouncedModuleSearch.trim());
      setPage(1);
    }
  }, [debouncedModuleSearch, statusKurikulum]);

  useEffect(() => {
    setParticipantPage(1);
  }, [debouncedParticipantSearch]);

  useEffect(() => {
    if (participantMethod === 'attendance-qr') {
      setSelectedAttendanceQR('');
      setAttendanceQRPage(1);
    } else if (participantMethod === 'certificate') {
      setSelectedCertificate('');
      setCertificatePage(1);
    } else if (participantMethod === 'enrollment') {
      setSelectedEnrollmentYear('');
      setEnrollmentYearPage(1);
    }
    setSelectedParticipants([]);
    setParticipantModuleAssignments({});
    setParticipantSearchInput('');
    setParticipantPage(1);
  }, [participantMethod]);

  const executeSearch = () => {
    if (!statusKurikulum) {
      if (searchInput.trim().length >= 2) {
        setSearchTerm(searchInput.trim());
        setPage(1);
      }
    }
  };

  // Handle Enter key for module search
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !statusKurikulum) {
      executeSearch();
    }
  };

  // Clear module search
  const clearModuleSearch = () => {
    setSearchInput('');
    setSearchTerm('');
    setPage(1);
  };

  const clearParticipantSearch = () => {
    setParticipantSearchInput('');
    setParticipantPage(1);
  };

  const {
    data: apiResponse,
    isLoading: apiLoading,
    error: moduleError
  } = useQuery({
    queryKey: ['moduleSearch', searchTerm, page],
    queryFn: async () => {
      const response = await httpClient.get('/assessment-schedules/list-module', {
        params: {
          PageNumber: page,
          PageSize: 15,
          search: searchTerm
        }
      });
      return response.data;
    },
    enabled: !statusKurikulum && searchTerm.length >= 2,
    staleTime: 30000
  });

  // Prepare module data with filtering for curriculum mode
  const { modules, totalPages, totalRecords, isLoading } = useMemo(() => {
    // Helper function to normalize module data
    const normalizeModule = (m: any): Module => ({
      moduleId: m.moduleId || m.moduleID || m.ModuleId || m.ModuleID || '',
      moduleName: m.moduleName || m.ModuleName || '',
      courseCode: m.courseCode || m.CourseCode || '',
      description: m.description || m.Description || '',
      programId: m.programId || m.programID || m.ProgramId || m.ProgramID || '',
      programName: m.programName || m.ProgramName || '',
      beelajarId:
        m.beelajarId || m.BeelajarId || m.beelajarID || m.BeelajarID || m.beelajar_id || null
    });

    if (statusKurikulum) {
      const programModules = (programModulesData || []).map(normalizeModule);
      const filteredModules =
        searchTerm.length > 0
          ? programModules.filter(
              (module: Module) =>
                module.moduleName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (module.courseCode &&
                  module.courseCode.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (module.description &&
                  module.description.toLowerCase().includes(searchTerm.toLowerCase()))
            )
          : programModules;

      const pageSize = 15;
      const totalRec = filteredModules.length;
      const totalPgs = Math.ceil(totalRec / pageSize);
      const startIndex = (page - 1) * pageSize;
      const paginatedModules = filteredModules.slice(startIndex, startIndex + pageSize);

      return {
        modules: paginatedModules,
        totalPages: totalPgs,
        totalRecords: totalRec,
        isLoading: false
      };
    } else {
      const normalizedModules = (apiResponse?.data || []).map(normalizeModule);
      return {
        modules: normalizedModules,
        totalPages: apiResponse?.totalPages || 0,
        totalRecords: apiResponse?.totalRecords || 0,
        isLoading: apiLoading
      };
    }
  }, [statusKurikulum, programModulesData, searchTerm, page, apiResponse, apiLoading]);

  const {
    data: attendanceQRResponse,
    isLoading: isLoadingAttendanceQRList,
    error: attendanceQRError
  } = useQuery({
    queryKey: ['attendanceQRList', selectedModules, attendanceQRPage],
    queryFn: async () => {
      if (selectedModules.length === 0) return null;

      const courseId = selectedModules[0];
      const response = await httpClient.get('/assessment-schedules/qr-participants', {
        params: {
          pageNumber: attendanceQRPage,
          pageSize: 15,
          courseid: courseId,
          attendanceqrid: ''
        }
      });
      return response.data;
    },
    enabled:
      activeTab === 'participants' &&
      selectedModules.length > 0 &&
      participantMethod === 'attendance-qr',
    staleTime: 30000
  });

  const {
    data: certificateResponse,
    isLoading: isLoadingCertificateList,
    error: certificateError
  } = useQuery({
    queryKey: ['certificateList', selectedModules, certificatePage],
    queryFn: async () => {
      if (selectedModules.length === 0) return null;

      const courseId = selectedModules[0];
      const response = await httpClient.get('/assessment-schedules/customcert', {
        params: {
          pageNumber: certificatePage,
          pageSize: 15,
          courseid: courseId
        }
      });
      return response.data;
    },
    enabled:
      activeTab === 'participants' &&
      selectedModules.length > 0 &&
      participantMethod === 'certificate',
    staleTime: 30000
  });

  // Enrollment: Years list
  const {
    data: enrollmentYearsResponse,
    isLoading: isLoadingEnrollmentYears,
    error: enrollmentYearsError
  } = useQuery({
    queryKey: ['enrollmentYears', selectedModules, enrollmentYearPage],
    queryFn: async () => {
      if (selectedModules.length === 0) return null;

      const courseId = selectedModules[0];
      const response = await httpClient.get('/assessment-schedules/year-enrollment', {
        params: {
          pageNumber: enrollmentYearPage,
          pageSize: 15,
          courseid: courseId
        }
      });
      return response.data;
    },
    enabled:
      activeTab === 'participants' &&
      selectedModules.length > 0 &&
      participantMethod === 'enrollment',
    staleTime: 30000
  });

  const {
    data: certificateParticipantsResponse,
    isLoading: isLoadingCertificateParticipants,
    error: certificateParticipantsError
  } = useQuery({
    queryKey: ['certificateParticipants', selectedCertificate, participantPage],
    queryFn: async () => {
      if (!selectedCertificate || selectedModules.length === 0) return null;

      const courseId = selectedModules[0];
      const response = await httpClient.get('/assessment-schedules/customcert', {
        params: {
          pageNumber: participantPage,
          pageSize: 15,
          customcertid: selectedCertificate,
          courseid: courseId
        }
      });
      return response.data;
    },
    enabled:
      activeTab === 'participants' &&
      selectedModules.length > 0 &&
      participantMethod === 'certificate' &&
      selectedCertificate !== '',
    staleTime: 30000
  });

  // Enrollment: Participants by selected year
  const {
    data: enrollmentParticipantsResponse,
    isLoading: isLoadingEnrollmentParticipants,
    error: enrollmentParticipantsError
  } = useQuery({
    queryKey: ['enrollmentParticipants', selectedEnrollmentYear, participantPage],
    queryFn: async () => {
      if (!selectedEnrollmentYear || selectedModules.length === 0) return null;

      const courseId = selectedModules[0];
      const response = await httpClient.get('/assessment-schedules/year-enrollment', {
        params: {
          pageNumber: participantPage,
          pageSize: 15,
          courseid: courseId,
          year: selectedEnrollmentYear
        }
      });
      return response.data;
    },
    enabled:
      activeTab === 'participants' &&
      selectedModules.length > 0 &&
      participantMethod === 'enrollment' &&
      selectedEnrollmentYear !== '',
    staleTime: 30000
  });

  const {
    data: attendanceParticipantsResponse,
    isLoading: isLoadingAttendanceParticipants,
    error: attendanceParticipantsError
  } = useQuery({
    queryKey: ['attendanceParticipants', selectedAttendanceQR, participantPage],
    queryFn: async () => {
      if (!selectedAttendanceQR || selectedModules.length === 0) return null;

      const courseId = selectedModules[0];
      const response = await httpClient.get('/assessment-schedules/qr-participants', {
        params: {
          pageNumber: participantPage,
          pageSize: 15,
          courseid: courseId,
          attendanceqrid: selectedAttendanceQR
        }
      });
      return response.data;
    },
    enabled:
      activeTab === 'participants' &&
      selectedModules.length > 0 &&
      participantMethod === 'attendance-qr' &&
      selectedAttendanceQR !== '',
    staleTime: 30000
  });

  const {
    data: allParticipantsResponse,
    isLoading: isLoadingAllParticipants,
    error: participantError
  } = useQuery({
    queryKey: ['allCourseParticipants', selectedModules, participantMethod],
    queryFn: async () => {
      if (selectedModules.length === 0) return null;

      const courseId = selectedModules[0];
      const response = await httpClient.get('/assessment-schedules/course-participants', {
        params: {
          pageNumber: 1,
          pageSize: 1000,
          courseid: courseId
        }
      });
      return response.data;
    },
    enabled:
      activeTab === 'participants' &&
      selectedModules.length > 0 &&
      participantMethod === 'completion',
    staleTime: 30000
  });

  const { filteredParticipants, participantsTotalPages, participantsTotalRecords } = useMemo(() => {
    let allParticipants: CourseParticipant[] = [];

    if (participantMethod === 'completion') {
      allParticipants = allParticipantsResponse?.data || [];
    } else if (participantMethod === 'attendance-qr' && selectedAttendanceQR) {
      allParticipants = (attendanceParticipantsResponse?.data || []).map((participant: any) => ({
        userId: participant.userId,
        fullname: participant.fullName,
        email: participant.email
      }));
    } else if (participantMethod === 'certificate' && selectedCertificate) {
      allParticipants = (certificateParticipantsResponse?.data || []).map((participant: any) => ({
        userId: participant.userId,
        fullname: participant.fullName,
        email: participant.email
      }));
    } else if (participantMethod === 'enrollment' && selectedEnrollmentYear) {
      allParticipants = (enrollmentParticipantsResponse?.data || []).map((participant: any) => ({
        userId: participant.userId,
        fullname: participant.fullName,
        email: participant.email
      }));
    }

    let filtered = allParticipants;
    if (debouncedParticipantSearch.trim()) {
      const searchLower = debouncedParticipantSearch.toLowerCase();
      filtered = allParticipants.filter(
        (participant: CourseParticipant) =>
          participant.fullname.toLowerCase().includes(searchLower) ||
          participant.email.toLowerCase().includes(searchLower) ||
          participant.userId.toString().includes(searchLower)
      );
    }

    if (
      participantMethod === 'attendance-qr' ||
      participantMethod === 'certificate' ||
      participantMethod === 'enrollment'
    ) {
      const apiResponse =
        participantMethod === 'attendance-qr'
          ? attendanceParticipantsResponse
          : participantMethod === 'certificate'
            ? certificateParticipantsResponse
            : enrollmentParticipantsResponse;

      return {
        filteredParticipants: filtered,
        participantsTotalPages: apiResponse?.totalPages || 0,
        participantsTotalRecords: apiResponse?.totalRecords || 0
      };
    }

    const pageSize = 15;
    const totalRec = filtered.length;
    const totalPgs = Math.ceil(totalRec / pageSize);
    const startIndex = (participantPage - 1) * pageSize;
    const paginatedParticipants = filtered.slice(startIndex, startIndex + pageSize);

    return {
      filteredParticipants: paginatedParticipants,
      participantsTotalPages: totalPgs,
      participantsTotalRecords: totalRec
    };
  }, [
    allParticipantsResponse?.data,
    attendanceParticipantsResponse?.data,
    attendanceParticipantsResponse?.totalPages,
    attendanceParticipantsResponse?.totalRecords,
    certificateParticipantsResponse?.data,
    certificateParticipantsResponse?.totalPages,
    certificateParticipantsResponse?.totalRecords,
    enrollmentParticipantsResponse?.data,
    enrollmentParticipantsResponse?.totalPages,
    enrollmentParticipantsResponse?.totalRecords,
    debouncedParticipantSearch,
    participantPage,
    participantMethod,
    selectedAttendanceQR,
    selectedCertificate,
    selectedEnrollmentYear
  ]);

  const toggleModule = (moduleId: string) => {
    setSelectedModules((prev) => {
      if (prev.includes(moduleId)) {
        return [];
      }
      return [moduleId];
    });
  };

  const skipParticipantSelection = () => {
    const chosenModules = modules.filter((m: Module) => selectedModules.includes(m.moduleId));
    onModulesSelected(chosenModules, 'add');

    if (onParticipantsSelected) {
      onParticipantsSelected([]);
    }

    onClose();
  };

  // Toggle participant selection
  const toggleParticipant = (participantId: string) => {
    setSelectedParticipants((prev) => {
      if (prev.includes(participantId)) {
        setParticipantModuleAssignments((prevAssignments) => {
          const newAssignments = { ...prevAssignments };
          delete newAssignments[participantId];
          return newAssignments;
        });
        return prev.filter((id) => id !== participantId);
      } else {
        setParticipantModuleAssignments((prevAssignments) => ({
          ...prevAssignments,
          [participantId]: [...selectedModules]
        }));
        return [...prev, participantId];
      }
    });
  };

  // Toggle module assignment for a specific participant
  const toggleParticipantModule = (participantId: string, moduleId: string) => {
    setParticipantModuleAssignments((prev) => {
      const currentAssignments = prev[participantId] || [];
      const newAssignments = currentAssignments.includes(moduleId)
        ? currentAssignments.filter((id) => id !== moduleId)
        : [...currentAssignments, moduleId];

      return {
        ...prev,
        [participantId]: newAssignments
      };
    });
  };

  // Select all participants on current page
  const toggleSelectAllParticipants = () => {
    const pageParticipantIds = filteredParticipants.map((p: CourseParticipant) => String(p.userId));
    const allSelected = pageParticipantIds.every((id: string) => selectedParticipants.includes(id));

    if (allSelected) {
      setSelectedParticipants((prev) => prev.filter((id) => !pageParticipantIds.includes(id)));
      setParticipantModuleAssignments((prev) => {
        const newAssignments = { ...prev };
        pageParticipantIds.forEach((id: string) => delete newAssignments[id]);
        return newAssignments;
      });
    } else {
      setSelectedParticipants((prev) => [...new Set([...prev, ...pageParticipantIds])]);
      setParticipantModuleAssignments((prev) => {
        const newAssignments = { ...prev };
        pageParticipantIds.forEach((id: string) => {
          if (!prev[id]) {
            newAssignments[id] = [...selectedModules];
          }
        });
        return newAssignments;
      });
    }
  };

  // Proceed to participant selection
  const proceedToParticipants = () => {
    if (selectedModules.length > 0) {
      setActiveTab('participants');
      setParticipantPage(1);
      setSelectedParticipants([]);
      setParticipantSearchInput('');
    }
  };

  // Go back to module selection
  const backToModules = () => {
    setActiveTab('modules');
    setSelectedParticipants([]);
    setParticipantSearchInput('');
  };

  const confirmSelection = () => {
    const chosenModules = modules.filter((m: Module) => selectedModules.includes(m.moduleId));
    onModulesSelected(chosenModules, 'add');

    if (onParticipantsSelected && selectedParticipants.length > 0) {
      const chosenParticipants: SelectedParticipant[] = filteredParticipants
        .filter((p: CourseParticipant) => selectedParticipants.includes(String(p.userId)))
        .map((p: CourseParticipant) => ({
          binusianId: String(p.userId),
          name: p.fullname,
          email: p.email,
          listModule: participantModuleAssignments[String(p.userId)] || []
        }));
      onParticipantsSelected(chosenParticipants);
    }

    onClose();
  };

  // Cancel and close
  const cancelAndClose = () => {
    setSelectedModules([...selectedModuleIds]);
    setSelectedParticipants([]);
    onClose();
  };

  const placeholderText = statusKurikulum
    ? 'Search courses from selected program...'
    : 'Enter module name to search (min 2 chars)...';
  const instructionText = statusKurikulum ? 'courses' : 'modules';

  // Participant selection helpers
  const allParticipantsSelected =
    filteredParticipants.length > 0 &&
    filteredParticipants.every((p: CourseParticipant) =>
      selectedParticipants.includes(String(p.userId))
    );
  const someParticipantsSelected = filteredParticipants.some((p: CourseParticipant) =>
    selectedParticipants.includes(String(p.userId))
  );

  const isLastEnrollmentYearsPage =
    (enrollmentYearsResponse?.totalPages || 0) > 0 &&
    enrollmentYearPage === enrollmentYearsResponse.totalPages;

  return (
    <Modal opened={opened} onClose={cancelAndClose} title={title} size='xl' padding='lg'>
      <Stack gap='md'>
        <Tabs value={activeTab} onChange={() => {}}>
          <Tabs.List>
            <Tabs.Tab value='modules' leftSection={<IconSearch size={16} />}>
              Select {statusKurikulum ? 'Courses' : 'Modules'}
            </Tabs.Tab>
            {/* <Tabs.Tab
              value='participants'
              leftSection={<IconUsers size={16} />}
              disabled={selectedModules.length === 0}
            >
              Select Participants{' '}
              {selectedParticipants.length > 0 && `(${selectedParticipants.length})`}
            </Tabs.Tab> */}
          </Tabs.List>

          {/* MODULE SELECTION TAB */}
          <Tabs.Panel value='modules'>
            <Stack gap='md' mt='md'>
              {/* Program Info for Curriculum Mode */}
              {statusKurikulum && programId && (
                <Alert icon={<IconInfoCircle size={16} />} color='blue' variant='light'>
                  Showing courses from the selected program only.
                </Alert>
              )}

              {/* Search Section */}
              <Group>
                <TextInput
                  style={{ flex: 1 }}
                  placeholder={placeholderText}
                  leftSection={<IconSearch size={16} />}
                  rightSection={
                    searchInput && (
                      <ActionIcon
                        size='sm'
                        variant='transparent'
                        onClick={clearModuleSearch}
                        title='Clear search'
                      >
                        <IconX size={16} />
                      </ActionIcon>
                    )
                  }
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                {!statusKurikulum && (
                  <Button
                    onClick={executeSearch}
                    disabled={searchInput.trim().length < 2}
                    loading={isLoading}
                  >
                    Search
                  </Button>
                )}
              </Group>

              {/* Search Results Summary for Curriculum Mode */}
              {statusKurikulum && searchTerm && (
                <Paper withBorder p='sm' bg='gray.0'>
                  <Text size='sm' c='dimmed'>
                    {totalRecords > 0
                      ? `Found ${totalRecords} courses matching "${searchTerm}"`
                      : `No courses found matching "${searchTerm}"`}
                  </Text>
                </Paper>
              )}

              {/* Instructions & Errors */}
              {statusKurikulum && (!programModulesData || programModulesData.length === 0) && (
                <Alert icon={<IconInfoCircle size={16} />} color='orange'>
                  No courses available in the selected program.
                </Alert>
              )}

              {!statusKurikulum && searchInput.length > 0 && searchInput.length < 2 && (
                <Alert icon={<IconInfoCircle size={16} />} color='blue'>
                  Please enter at least 2 characters to search
                </Alert>
              )}

              {moduleError && (
                <Alert icon={<IconX size={16} />} color='red'>
                  Search failed. Please try again.
                </Alert>
              )}

              {/* Results */}
              {(statusKurikulum || searchTerm.length >= 2) && (
                <Stack gap='sm'>
                  <Group justify='space-between'>
                    <Text size='sm' c='dimmed'>
                      {isLoading
                        ? 'Searching...'
                        : `${totalRecords} ${instructionText} found${statusKurikulum ? ' in selected program' : searchTerm ? ` for "${searchTerm}"` : ''}`}
                    </Text>
                    {selectedModules.length > 0 && (
                      <Badge variant='light' color='blue'>
                        {selectedModules.length} selected
                      </Badge>
                    )}
                  </Group>

                  <div style={{ position: 'relative', minHeight: 200 }}>
                    <LoadingOverlay visible={isLoading} />

                    {modules.length > 0 ? (
                      <Table striped highlightOnHover>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th style={{ width: 50 }}>Select</Table.Th>
                            <Table.Th>{statusKurikulum ? 'Course Name' : 'Module Name'}</Table.Th>
                            <Table.Th>Course Code</Table.Th>
                            <Table.Th>Beelajar ID</Table.Th>
                            <Table.Th>Description</Table.Th>
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {modules.map((module: Module) => {
                            const hasBeelajarId =
                              module.beelajarId !== null && module.beelajarId !== undefined;

                            return (
                              <Table.Tr key={module.moduleId}>
                                <Table.Td>
                                  <Checkbox
                                    checked={selectedModules.includes(module.moduleId)}
                                    onChange={() => toggleModule(module.moduleId)}
                                  />
                                </Table.Td>
                                <Table.Td>
                                  <Text fw={500} c={!hasBeelajarId ? 'dimmed' : undefined}>
                                    {module.moduleName}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Text size='sm' c='dimmed'>
                                    {module.courseCode || '-'}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Text
                                    size='sm'
                                    c={hasBeelajarId ? 'blue' : 'red'}
                                    fw={hasBeelajarId ? 500 : 400}
                                  >
                                    {hasBeelajarId ? module.beelajarId : 'No Beelajar ID'}
                                  </Text>
                                </Table.Td>
                                <Table.Td>
                                  <Text size='sm' c='dimmed' lineClamp={2}>
                                    {module.description || '-'}
                                  </Text>
                                </Table.Td>
                              </Table.Tr>
                            );
                          })}
                        </Table.Tbody>
                      </Table>
                    ) : (
                      !isLoading && (
                        <Text ta='center' c='dimmed' py='xl'>
                          No {instructionText} found
                          {statusKurikulum
                            ? searchTerm
                              ? ` matching "${searchTerm}"`
                              : ' in the selected program'
                            : searchTerm
                              ? ` for "${searchTerm}"`
                              : ''}
                        </Text>
                      )
                    )}
                  </div>

                  {/* Module Pagination */}
                  {totalPages > 1 && (
                    <Group justify='center'>
                      <Button
                        variant='outline'
                        size='sm'
                        disabled={page === 1}
                        onClick={() => setPage((p) => p - 1)}
                      >
                        Previous
                      </Button>
                      <Text size='sm'>
                        Page {page} of {totalPages}
                      </Text>
                      <Button
                        variant='outline'
                        size='sm'
                        disabled={page === totalPages}
                        onClick={() => setPage((p) => p + 1)}
                      >
                        Next
                      </Button>
                    </Group>
                  )}
                </Stack>
              )}
            </Stack>
          </Tabs.Panel>

          {/* PARTICIPANT SELECTION TAB */}
          <Tabs.Panel value='participants'>
            <Stack gap='md' mt='md'>
              <Paper withBorder p='md' radius='md'>
                <Title order={5} mb='xs'>
                  Selected {statusKurikulum ? 'Courses' : 'Modules'}
                </Title>
                <Group gap='xs'>
                  {selectedModules.map((moduleId) => {
                    const module =
                      modules.find((m: any) => m.moduleId === moduleId) ||
                      programModulesData.find((m) => m.moduleId === moduleId);
                    return (
                      <Badge key={moduleId} variant='light' color='blue'>
                        {module?.moduleName || moduleId}
                      </Badge>
                    );
                  })}
                </Group>
              </Paper>

              <Divider />

              <Title order={5}>Select Participant Source</Title>
              <Radio.Group
                value={participantMethod}
                onChange={(value) => setParticipantMethod(value as ParticipantSelectionMethod)}
              >
                <Stack gap='xs'>
                  <Radio value='completion' label='By Completion Course' />
                  <Radio value='attendance-qr' label='By Attendance QR Course' />
                  <Radio value='enrollment' label='By Enrollment' />
                  <Radio value='certificate' label='By Course Certificate' />
                </Stack>
              </Radio.Group>

              {participantMethod === 'completion' && (
                <Stack gap='md'>
                  {/* Participant Search Input */}
                  <TextInput
                    placeholder='Search participants by name or email'
                    leftSection={<IconSearch size={16} />}
                    rightSection={
                      participantSearchInput && (
                        <ActionIcon
                          size='sm'
                          variant='transparent'
                          onClick={clearParticipantSearch}
                          title='Clear search'
                        >
                          <IconX size={16} />
                        </ActionIcon>
                      )
                    }
                    value={participantSearchInput}
                    onChange={(e) => setParticipantSearchInput(e.target.value)}
                  />

                  {/* Participant Search Results Summary */}
                  {debouncedParticipantSearch && (
                    <Paper withBorder p='sm' bg='gray.0'>
                      <Text size='sm' c='dimmed'>
                        {participantsTotalRecords > 0
                          ? `Found ${participantsTotalRecords} participants matching "${debouncedParticipantSearch}"`
                          : `No participants found matching "${debouncedParticipantSearch}"`}
                      </Text>
                    </Paper>
                  )}

                  <Group justify='space-between'>
                    <Text size='sm' c='dimmed'>
                      {isLoadingAllParticipants
                        ? 'Loading participants...'
                        : `${participantsTotalRecords} participants found`}
                    </Text>
                    {selectedParticipants.length > 0 && (
                      <Badge variant='light' color='green'>
                        {selectedParticipants.length} selected
                      </Badge>
                    )}
                  </Group>

                  <div style={{ position: 'relative', minHeight: 200 }}>
                    <LoadingOverlay visible={isLoadingAllParticipants} />

                    {filteredParticipants.length > 0 ? (
                      <Stack gap='sm'>
                        <Table striped highlightOnHover>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th style={{ width: 50 }}>
                                <Checkbox
                                  checked={allParticipantsSelected}
                                  indeterminate={
                                    someParticipantsSelected && !allParticipantsSelected
                                  }
                                  onChange={toggleSelectAllParticipants}
                                />
                              </Table.Th>
                              <Table.Th>Name</Table.Th>
                              <Table.Th>Email</Table.Th>
                              <Table.Th>User ID</Table.Th>
                              <Table.Th>
                                Assigned {statusKurikulum ? 'Courses' : 'Modules'}
                              </Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            {filteredParticipants.map((participant: CourseParticipant) => {
                              const participantId = String(participant.userId);
                              const isSelected = selectedParticipants.includes(participantId);
                              const assignedModules =
                                participantModuleAssignments[participantId] || [];

                              return (
                                <Table.Tr key={participant.userId}>
                                  <Table.Td>
                                    <Checkbox
                                      checked={isSelected}
                                      onChange={() => toggleParticipant(participantId)}
                                    />
                                  </Table.Td>
                                  <Table.Td>
                                    <Group>
                                      <Avatar color='#7B2183' radius='xl' size='sm'>
                                        {participant.fullname.charAt(0)}
                                      </Avatar>
                                      <Text fw={500}>{participant.fullname}</Text>
                                    </Group>
                                  </Table.Td>
                                  <Table.Td>
                                    <Text size='sm' c='dimmed'>
                                      {participant.email}
                                    </Text>
                                  </Table.Td>
                                  <Table.Td>
                                    <Text size='sm' c='dimmed'>
                                      {participant.userId}
                                    </Text>
                                  </Table.Td>
                                  <Table.Td>
                                    {isSelected ? (
                                      <Stack gap='xs'>
                                        <Group gap='xs'>
                                          {selectedModules.map((moduleId) => {
                                            const module =
                                              modules.find((m: any) => m.moduleId === moduleId) ||
                                              programModulesData.find(
                                                (m) => m.moduleId === moduleId
                                              );
                                            const isAssigned = assignedModules.includes(moduleId);

                                            return (
                                              <Checkbox
                                                key={moduleId}
                                                size='xs'
                                                label={
                                                  <Text size='xs'>
                                                    {module?.moduleName || moduleId}
                                                  </Text>
                                                }
                                                checked={isAssigned}
                                                onChange={() =>
                                                  toggleParticipantModule(participantId, moduleId)
                                                }
                                              />
                                            );
                                          })}
                                        </Group>
                                        <Text size='xs' c='dimmed'>
                                          {assignedModules.length} of {selectedModules.length}{' '}
                                          assigned
                                        </Text>
                                      </Stack>
                                    ) : (
                                      <Text size='xs' c='dimmed'>
                                        Select participant to assign modules
                                      </Text>
                                    )}
                                  </Table.Td>
                                </Table.Tr>
                              );
                            })}
                          </Table.Tbody>
                        </Table>
                      </Stack>
                    ) : (
                      !isLoadingAllParticipants && (
                        <Text ta='center' c='dimmed' py='xl'>
                          {debouncedParticipantSearch
                            ? `No participants found matching "${debouncedParticipantSearch}"`
                            : 'No participants found for the selected courses'}
                        </Text>
                      )
                    )}
                  </div>

                  {/* Participant Pagination */}
                  {participantsTotalPages > 1 && (
                    <Group justify='center'>
                      <Button
                        variant='outline'
                        size='sm'
                        disabled={participantPage === 1}
                        onClick={() => setParticipantPage((p) => p - 1)}
                      >
                        Previous
                      </Button>
                      <Text size='sm'>
                        Page {participantPage} of {participantsTotalPages}
                      </Text>
                      <Button
                        variant='outline'
                        size='sm'
                        disabled={participantPage === participantsTotalPages}
                        onClick={() => setParticipantPage((p) => p + 1)}
                      >
                        Next
                      </Button>
                    </Group>
                  )}
                </Stack>
              )}

              {participantMethod === 'certificate' && (
                <Stack gap='md'>
                  {/* Step 1: Select Certificate */}
                  {!selectedCertificate && (
                    <Stack gap='md'>
                      <Paper withBorder p='md' radius='md' bg='blue.0'>
                        <Group>
                          <IconInfoCircle size={20} color='purple' />
                          <Text size='sm' fw={500}>
                            Step 1: Select a Certificate
                          </Text>
                        </Group>
                        <Text size='sm' c='dimmed' mt='xs'>
                          Choose from available course certificates to load participants
                        </Text>
                      </Paper>

                      <Group justify='space-between'>
                        <Text size='sm' c='dimmed'>
                          {isLoadingCertificateList
                            ? 'Loading certificates...'
                            : `${certificateResponse?.totalRecords || 0} certificates found`}
                        </Text>
                      </Group>

                      <div style={{ position: 'relative', minHeight: 200 }}>
                        <LoadingOverlay visible={isLoadingCertificateList} />

                        {certificateResponse?.data && certificateResponse.data.length > 0 ? (
                          <Stack gap='sm'>
                            <Table striped highlightOnHover>
                              <Table.Thead>
                                <Table.Tr>
                                  <Table.Th>Select</Table.Th>
                                  <Table.Th>Certificate Name</Table.Th>
                                  <Table.Th>Certificate ID</Table.Th>
                                </Table.Tr>
                              </Table.Thead>
                              <Table.Tbody>
                                {certificateResponse.data.map((cert: any) => (
                                  <Table.Tr key={cert.id}>
                                    <Table.Td>
                                      <Button
                                        size='xs'
                                        variant='light'
                                        color='purple'
                                        onClick={() => {
                                          setSelectedCertificate(cert.id.toString());
                                          setParticipantPage(1); // Reset page when selecting new certificate
                                        }}
                                      >
                                        Select
                                      </Button>
                                    </Table.Td>
                                    <Table.Td>
                                      <Text fw={500}>{cert.name}</Text>
                                    </Table.Td>
                                    <Table.Td>
                                      <Text size='sm' c='dimmed'>
                                        {cert.id}
                                      </Text>
                                    </Table.Td>
                                  </Table.Tr>
                                ))}
                              </Table.Tbody>
                            </Table>

                            {/* Certificate List Pagination */}
                            {certificateResponse.totalPages > 1 && (
                              <Group justify='center'>
                                <Button
                                  variant='outline'
                                  size='sm'
                                  disabled={certificatePage === 1}
                                  onClick={() => setCertificatePage((p) => p - 1)}
                                >
                                  Previous
                                </Button>
                                <Text size='sm'>
                                  Page {certificatePage} of {certificateResponse.totalPages}
                                </Text>
                                <Button
                                  variant='outline'
                                  size='sm'
                                  disabled={certificatePage === certificateResponse.totalPages}
                                  onClick={() => setCertificatePage((p) => p + 1)}
                                >
                                  Next
                                </Button>
                              </Group>
                            )}
                          </Stack>
                        ) : (
                          !isLoadingCertificateList && (
                            <Text ta='center' c='dimmed' py='xl'>
                              No certificates found for the selected course
                            </Text>
                          )
                        )}
                      </div>

                      {certificateError && (
                        <Alert icon={<IconX size={16} />} color='red'>
                          Failed to load certificates. Please try again.
                        </Alert>
                      )}
                    </Stack>
                  )}

                  {/* Step 2: Select Participants from chosen Certificate */}
                  {selectedCertificate && (
                    <Stack gap='md'>
                      <Paper withBorder p='md' radius='md' bg='green.0'>
                        <Group justify='space-between'>
                          <Group>
                            <IconCheck size={20} color='green' />
                            <div>
                              <Text size='sm' fw={500}>
                                Step 2: Select Participants
                              </Text>
                              <Text size='xs' c='dimmed'>
                                Selected Certificate:{' '}
                                {
                                  certificateResponse?.data?.find(
                                    (cert: any) => cert.id.toString() === selectedCertificate
                                  )?.name
                                }
                              </Text>
                            </div>
                          </Group>
                          <Button
                            size='xs'
                            variant='outline'
                            onClick={() => {
                              setSelectedCertificate('');
                              setSelectedParticipants([]);
                              setParticipantModuleAssignments({});
                            }}
                          >
                            Change Certificate
                          </Button>
                        </Group>
                      </Paper>

                      {/* Participant Search Input */}
                      <TextInput
                        placeholder='Search participants by name or email'
                        leftSection={<IconSearch size={16} />}
                        rightSection={
                          participantSearchInput && (
                            <ActionIcon
                              size='sm'
                              variant='transparent'
                              onClick={clearParticipantSearch}
                              title='Clear search'
                            >
                              <IconX size={16} />
                            </ActionIcon>
                          )
                        }
                        value={participantSearchInput}
                        onChange={(e) => setParticipantSearchInput(e.target.value)}
                      />

                      {/* Use the same participant selection table as attendance method */}
                      {/* Participant Search Results Summary */}
                      {debouncedParticipantSearch && (
                        <Paper withBorder p='sm' bg='gray.0'>
                          <Text size='sm' c='dimmed'>
                            {participantsTotalRecords > 0
                              ? `Found ${participantsTotalRecords} participants matching "${debouncedParticipantSearch}"`
                              : `No participants found matching "${debouncedParticipantSearch}"`}
                          </Text>
                        </Paper>
                      )}

                      <Group justify='space-between'>
                        <Text size='sm' c='dimmed'>
                          {isLoadingCertificateParticipants
                            ? 'Loading participants...'
                            : `${participantsTotalRecords} participants found with this certificate`}
                        </Text>
                        {selectedParticipants.length > 0 && (
                          <Badge variant='light' color='green'>
                            {selectedParticipants.length} selected
                          </Badge>
                        )}
                      </Group>

                      <div style={{ position: 'relative', minHeight: 200 }}>
                        <LoadingOverlay visible={isLoadingCertificateParticipants} />

                        {filteredParticipants.length > 0 ? (
                          <Stack gap='sm'>
                            {/* Same participant table as attendance method */}
                            <Table striped highlightOnHover>
                              <Table.Thead>
                                <Table.Tr>
                                  <Table.Th style={{ width: 50 }}>
                                    <Checkbox
                                      checked={allParticipantsSelected}
                                      indeterminate={
                                        someParticipantsSelected && !allParticipantsSelected
                                      }
                                      onChange={toggleSelectAllParticipants}
                                    />
                                  </Table.Th>
                                  <Table.Th>Name</Table.Th>
                                  <Table.Th>Email</Table.Th>
                                  <Table.Th>User ID</Table.Th>
                                  <Table.Th>
                                    Assigned {statusKurikulum ? 'Courses' : 'Modules'}
                                  </Table.Th>
                                </Table.Tr>
                              </Table.Thead>
                              <Table.Tbody>
                                {filteredParticipants.map((participant: CourseParticipant) => {
                                  const participantId = String(participant.userId);
                                  const isSelected = selectedParticipants.includes(participantId);
                                  const assignedModules =
                                    participantModuleAssignments[participantId] || [];

                                  return (
                                    <Table.Tr key={participant.userId}>
                                      <Table.Td>
                                        <Checkbox
                                          checked={isSelected}
                                          onChange={() => toggleParticipant(participantId)}
                                        />
                                      </Table.Td>
                                      <Table.Td>
                                        <Group>
                                          <Avatar color='#7B2183' radius='xl' size='sm'>
                                            {participant.fullname.charAt(0)}
                                          </Avatar>
                                          <Text fw={500}>{participant.fullname}</Text>
                                        </Group>
                                      </Table.Td>
                                      <Table.Td>
                                        <Text size='sm' c='dimmed'>
                                          {participant.email || 'No email'}
                                        </Text>
                                      </Table.Td>
                                      <Table.Td>
                                        <Text size='sm' c='dimmed'>
                                          {participant.userId}
                                        </Text>
                                      </Table.Td>
                                      <Table.Td>
                                        {isSelected ? (
                                          <Stack gap='xs'>
                                            <Group gap='xs'>
                                              {selectedModules.map((moduleId) => {
                                                const module =
                                                  modules.find(
                                                    (m: any) => m.moduleId === moduleId
                                                  ) ||
                                                  programModulesData.find(
                                                    (m) => m.moduleId === moduleId
                                                  );
                                                const isAssigned =
                                                  assignedModules.includes(moduleId);

                                                return (
                                                  <Checkbox
                                                    key={moduleId}
                                                    size='xs'
                                                    label={
                                                      <Text size='xs'>
                                                        {module?.moduleName || moduleId}
                                                      </Text>
                                                    }
                                                    checked={isAssigned}
                                                    onChange={() =>
                                                      toggleParticipantModule(
                                                        participantId,
                                                        moduleId
                                                      )
                                                    }
                                                  />
                                                );
                                              })}
                                            </Group>
                                            <Text size='xs' c='dimmed'>
                                              {assignedModules.length} of {selectedModules.length}{' '}
                                              assigned
                                            </Text>
                                          </Stack>
                                        ) : (
                                          <Text size='xs' c='dimmed'>
                                            Select participant to assign modules
                                          </Text>
                                        )}
                                      </Table.Td>
                                    </Table.Tr>
                                  );
                                })}
                              </Table.Tbody>
                            </Table>

                            {/* Certificate Participants Pagination */}
                            {participantsTotalPages > 1 && (
                              <Group justify='center'>
                                <Button
                                  variant='outline'
                                  size='sm'
                                  disabled={participantPage === 1}
                                  onClick={() => setParticipantPage((p) => p - 1)}
                                >
                                  Previous
                                </Button>
                                <Text size='sm'>
                                  Page {participantPage} of {participantsTotalPages}
                                </Text>
                                <Button
                                  variant='outline'
                                  size='sm'
                                  disabled={participantPage === participantsTotalPages}
                                  onClick={() => setParticipantPage((p) => p + 1)}
                                >
                                  Next
                                </Button>
                              </Group>
                            )}
                          </Stack>
                        ) : (
                          !isLoadingCertificateParticipants && (
                            <Text ta='center' c='dimmed' py='xl'>
                              {debouncedParticipantSearch
                                ? `No participants found matching "${debouncedParticipantSearch}"`
                                : 'No participants found with this certificate'}
                            </Text>
                          )
                        )}
                      </div>

                      {certificateParticipantsError && (
                        <Alert icon={<IconX size={16} />} color='red'>
                          Failed to load participants from certificate. Please try again.
                        </Alert>
                      )}
                    </Stack>
                  )}
                </Stack>
              )}

              {participantMethod === 'enrollment' && (
                <Stack gap='md'>
                  {/* Step 1: Select Enrollment Year */}
                  {!selectedEnrollmentYear && (
                    <Stack gap='md'>
                      <Paper withBorder p='md' radius='md' bg='blue.0'>
                        <Group>
                          <IconInfoCircle size={20} color='blue' />
                          <Text size='sm' fw={500}>
                            Step 1: Select an Enrollment Year
                          </Text>
                        </Group>
                        <Text size='sm' c='dimmed' mt='xs'>
                          Choose a year to load enrolled participants
                        </Text>
                      </Paper>

                      <Group justify='space-between'>
                        <Text size='sm' c='dimmed'>
                          {isLoadingEnrollmentYears
                            ? 'Loading years...'
                            : `${enrollmentYearsResponse?.totalRecords || 0} year(s) found`}
                        </Text>
                      </Group>

                      <div style={{ position: 'relative', minHeight: 200 }}>
                        <LoadingOverlay visible={isLoadingEnrollmentYears} />

                        {enrollmentYearsResponse?.data &&
                        enrollmentYearsResponse.data.length > 0 ? (
                          <Stack gap='sm'>
                            <Table striped highlightOnHover>
                              <Table.Thead>
                                <Table.Tr>
                                  <Table.Th>Select</Table.Th>
                                  <Table.Th>Year</Table.Th>
                                </Table.Tr>
                              </Table.Thead>
                              <Table.Tbody>
                                {enrollmentYearsResponse.data.map((item: any) => (
                                  <Table.Tr key={item.year}>
                                    <Table.Td>
                                      <Button
                                        size='xs'
                                        variant='light'
                                        onClick={() => {
                                          setSelectedEnrollmentYear(String(item.year));
                                          setParticipantPage(1);
                                        }}
                                      >
                                        Select
                                      </Button>
                                    </Table.Td>
                                    <Table.Td>
                                      <Text fw={500}>{item.year}</Text>
                                    </Table.Td>
                                  </Table.Tr>
                                ))}
                              </Table.Tbody>
                            </Table>

                            {enrollmentYearsResponse.totalPages > 1 && (
                              <Group justify='center'>
                                <Button
                                  variant='outline'
                                  size='sm'
                                  disabled={enrollmentYearPage === 1}
                                  onClick={() => setEnrollmentYearPage((p) => p - 1)}
                                >
                                  Previous
                                </Button>
                                <Text size='sm'>
                                  Page {enrollmentYearPage} of {enrollmentYearsResponse.totalPages}
                                </Text>
                                <Button
                                  variant='outline'
                                  size='sm'
                                  disabled={isLastEnrollmentYearsPage}
                                  onClick={() => setEnrollmentYearPage((p) => p + 1)}
                                >
                                  Next
                                </Button>
                              </Group>
                            )}
                          </Stack>
                        ) : (
                          !isLoadingEnrollmentYears && (
                            <Text ta='center' c='dimmed' py='xl'>
                              No enrollment years found for the selected course
                            </Text>
                          )
                        )}
                      </div>

                      {enrollmentYearsError && (
                        <Alert icon={<IconX size={16} />} color='red'>
                          Failed to load enrollment years. Please try again.
                        </Alert>
                      )}
                    </Stack>
                  )}

                  {/* Step 2: Select Participants from chosen Year */}
                  {selectedEnrollmentYear && (
                    <Stack gap='md'>
                      <Paper withBorder p='md' radius='md' bg='green.0'>
                        <Group justify='space-between'>
                          <Group>
                            <IconCheck size={20} color='green' />
                            <div>
                              <Text size='sm' fw={500}>
                                Step 2: Select Participants
                              </Text>
                              <Text size='xs' c='dimmed'>
                                Selected Year: {selectedEnrollmentYear}
                              </Text>
                            </div>
                          </Group>
                          <Button
                            size='xs'
                            variant='outline'
                            onClick={() => {
                              setSelectedEnrollmentYear('');
                              setSelectedParticipants([]);
                              setParticipantModuleAssignments({});
                            }}
                          >
                            Change Year
                          </Button>
                        </Group>
                      </Paper>

                      {/* Participant Search Input */}
                      <TextInput
                        placeholder='Search participants by name or email'
                        leftSection={<IconSearch size={16} />}
                        rightSection={
                          participantSearchInput && (
                            <ActionIcon
                              size='sm'
                              variant='transparent'
                              onClick={clearParticipantSearch}
                              title='Clear search'
                            >
                              <IconX size={16} />
                            </ActionIcon>
                          )
                        }
                        value={participantSearchInput}
                        onChange={(e) => setParticipantSearchInput(e.target.value)}
                      />

                      {/* Participant Search Results Summary */}
                      {debouncedParticipantSearch && (
                        <Paper withBorder p='sm' bg='gray.0'>
                          <Text size='sm' c='dimmed'>
                            {participantsTotalRecords > 0
                              ? `Found ${participantsTotalRecords} participants matching "${debouncedParticipantSearch}"`
                              : `No participants found matching "${debouncedParticipantSearch}"`}
                          </Text>
                        </Paper>
                      )}

                      <Group justify='space-between'>
                        <Text size='sm' c='dimmed'>
                          {isLoadingEnrollmentParticipants
                            ? 'Loading participants...'
                            : `${participantsTotalRecords} enrolled participants found`}
                        </Text>
                        {selectedParticipants.length > 0 && (
                          <Badge variant='light' color='green'>
                            {selectedParticipants.length} selected
                          </Badge>
                        )}
                      </Group>

                      <div style={{ position: 'relative', minHeight: 200 }}>
                        <LoadingOverlay visible={isLoadingEnrollmentParticipants} />

                        {filteredParticipants.length > 0 ? (
                          <Stack gap='sm'>
                            <Table striped highlightOnHover>
                              <Table.Thead>
                                <Table.Tr>
                                  <Table.Th style={{ width: 50 }}>
                                    <Checkbox
                                      checked={allParticipantsSelected}
                                      indeterminate={
                                        someParticipantsSelected && !allParticipantsSelected
                                      }
                                      onChange={toggleSelectAllParticipants}
                                    />
                                  </Table.Th>
                                  <Table.Th>Name</Table.Th>
                                  <Table.Th>Email</Table.Th>
                                  <Table.Th>User ID</Table.Th>
                                  <Table.Th>
                                    Assigned {statusKurikulum ? 'Courses' : 'Modules'}
                                  </Table.Th>
                                </Table.Tr>
                              </Table.Thead>
                              <Table.Tbody>
                                {filteredParticipants.map((participant: CourseParticipant) => {
                                  const participantId = String(participant.userId);
                                  const isSelected = selectedParticipants.includes(participantId);
                                  const assignedModules =
                                    participantModuleAssignments[participantId] || [];

                                  return (
                                    <Table.Tr key={participant.userId}>
                                      <Table.Td>
                                        <Checkbox
                                          checked={isSelected}
                                          onChange={() => toggleParticipant(participantId)}
                                        />
                                      </Table.Td>
                                      <Table.Td>
                                        <Group>
                                          <Avatar color='#7B2183' radius='xl' size='sm'>
                                            {participant.fullname.charAt(0)}
                                          </Avatar>
                                          <Text fw={500}>{participant.fullname}</Text>
                                        </Group>
                                      </Table.Td>
                                      <Table.Td>
                                        <Text size='sm' c='dimmed'>
                                          {participant.email || 'No email'}
                                        </Text>
                                      </Table.Td>
                                      <Table.Td>
                                        <Text size='sm' c='dimmed'>
                                          {participant.userId}
                                        </Text>
                                      </Table.Td>
                                      <Table.Td>
                                        {isSelected ? (
                                          <Stack gap='xs'>
                                            <Group gap='xs'>
                                              {selectedModules.map((moduleId) => {
                                                const module =
                                                  modules.find(
                                                    (m: any) => m.moduleId === moduleId
                                                  ) ||
                                                  programModulesData.find(
                                                    (m) => m.moduleId === moduleId
                                                  );
                                                const isAssigned =
                                                  assignedModules.includes(moduleId);

                                                return (
                                                  <Checkbox
                                                    key={moduleId}
                                                    size='xs'
                                                    label={
                                                      <Text size='xs'>
                                                        {module?.moduleName || moduleId}
                                                      </Text>
                                                    }
                                                    checked={isAssigned}
                                                    onChange={() =>
                                                      toggleParticipantModule(
                                                        participantId,
                                                        moduleId
                                                      )
                                                    }
                                                  />
                                                );
                                              })}
                                            </Group>
                                            <Text size='xs' c='dimmed'>
                                              {assignedModules.length} of {selectedModules.length}{' '}
                                              assigned
                                            </Text>
                                          </Stack>
                                        ) : (
                                          <Text size='xs' c='dimmed'>
                                            Select participant to assign modules
                                          </Text>
                                        )}
                                      </Table.Td>
                                    </Table.Tr>
                                  );
                                })}
                              </Table.Tbody>
                            </Table>

                            {/* Enrollment Participants Pagination */}
                            {participantsTotalPages > 1 && (
                              <Group justify='center'>
                                <Button
                                  variant='outline'
                                  size='sm'
                                  disabled={participantPage === 1}
                                  onClick={() => setParticipantPage((p) => p - 1)}
                                >
                                  Previous
                                </Button>
                                <Text size='sm'>
                                  Page {participantPage} of {participantsTotalPages}
                                </Text>
                                <Button
                                  variant='outline'
                                  size='sm'
                                  disabled={participantPage === participantsTotalPages}
                                  onClick={() => setParticipantPage((p) => p + 1)}
                                >
                                  Next
                                </Button>
                              </Group>
                            )}
                          </Stack>
                        ) : (
                          !isLoadingEnrollmentParticipants && (
                            <Text ta='center' c='dimmed' py='xl'>
                              {debouncedParticipantSearch
                                ? `No participants found matching "${debouncedParticipantSearch}"`
                                : 'No enrolled participants found for the selected year'}
                            </Text>
                          )
                        )}
                      </div>

                      {enrollmentParticipantsError && (
                        <Alert icon={<IconX size={16} />} color='red'>
                          Failed to load enrolled participants. Please try again.
                        </Alert>
                      )}
                    </Stack>
                  )}
                </Stack>
              )}

              {participantMethod === 'attendance-qr' && (
                <Stack gap='md'>
                  {/* Step 1: Select Attendance QR */}
                  {!selectedAttendanceQR && (
                    <Stack gap='md'>
                      <Paper withBorder p='md' radius='md' bg='blue.0'>
                        <Group>
                          <IconInfoCircle size={20} color='blue' />
                          <Text size='sm' fw={500}>
                            Step 1: Select an Attendance QR Session
                          </Text>
                        </Group>
                        <Text size='sm' c='dimmed' mt='xs'>
                          Choose from available attendance QR sessions to load participants
                        </Text>
                      </Paper>

                      <Group justify='space-between'>
                        <Text size='sm' c='dimmed'>
                          {isLoadingAttendanceQRList
                            ? 'Loading attendance sessions...'
                            : `${attendanceQRResponse?.totalRecords || 0} attendance sessions found`}
                        </Text>
                      </Group>

                      <div style={{ position: 'relative', minHeight: 200 }}>
                        <LoadingOverlay visible={isLoadingAttendanceQRList} />

                        {attendanceQRResponse?.data && attendanceQRResponse.data.length > 0 ? (
                          <Stack gap='sm'>
                            <Table striped highlightOnHover>
                              <Table.Thead>
                                <Table.Tr>
                                  <Table.Th>Select</Table.Th>
                                  <Table.Th>Attendance Session</Table.Th>
                                  <Table.Th>Session ID</Table.Th>
                                </Table.Tr>
                              </Table.Thead>
                              <Table.Tbody>
                                {attendanceQRResponse.data.map((qr: any) => (
                                  <Table.Tr key={qr.userId}>
                                    <Table.Td>
                                      <Button
                                        size='xs'
                                        variant='light'
                                        onClick={() => {
                                          setSelectedAttendanceQR(qr.userId);
                                          setParticipantPage(1);
                                        }}
                                      >
                                        Select
                                      </Button>
                                    </Table.Td>
                                    <Table.Td>
                                      <Text fw={500}>{qr.fullName}</Text>
                                    </Table.Td>
                                    <Table.Td>
                                      <Text size='sm' c='dimmed'>
                                        {qr.userId}
                                      </Text>
                                    </Table.Td>
                                  </Table.Tr>
                                ))}
                              </Table.Tbody>
                            </Table>

                            {attendanceQRResponse.totalPages > 1 && (
                              <Group justify='center'>
                                <Button
                                  variant='outline'
                                  size='sm'
                                  disabled={attendanceQRPage === 1}
                                  onClick={() => setAttendanceQRPage((p) => p - 1)}
                                >
                                  Previous
                                </Button>
                                <Text size='sm'>
                                  Page {attendanceQRPage} of {attendanceQRResponse.totalPages}
                                </Text>
                                <Button
                                  variant='outline'
                                  size='sm'
                                  disabled={attendanceQRPage === attendanceQRResponse.totalPages}
                                  onClick={() => setAttendanceQRPage((p) => p + 1)}
                                >
                                  Next
                                </Button>
                              </Group>
                            )}
                          </Stack>
                        ) : (
                          !isLoadingAttendanceQRList && (
                            <Text ta='center' c='dimmed' py='xl'>
                              No attendance QR sessions found for the selected course
                            </Text>
                          )
                        )}
                      </div>

                      {attendanceQRError && (
                        <Alert icon={<IconX size={16} />} color='red'>
                          Failed to load attendance QR sessions. Please try again.
                        </Alert>
                      )}
                    </Stack>
                  )}

                  {/* Step 2: Select Participants from chosen QR */}
                  {selectedAttendanceQR && (
                    <Stack gap='md'>
                      <Paper withBorder p='md' radius='md' bg='green.0'>
                        <Group justify='space-between'>
                          <Group>
                            <IconCheck size={20} color='green' />
                            <div>
                              <Text size='sm' fw={500}>
                                Step 2: Select Participants
                              </Text>
                              <Text size='xs' c='dimmed'>
                                Selected QR:{' '}
                                {
                                  attendanceQRResponse?.data?.find(
                                    (qr: any) => qr.userId === selectedAttendanceQR
                                  )?.fullName
                                }
                              </Text>
                            </div>
                          </Group>
                          <Button
                            size='xs'
                            variant='outline'
                            onClick={() => {
                              setSelectedAttendanceQR('');
                              setSelectedParticipants([]);
                              setParticipantModuleAssignments({});
                            }}
                          >
                            Change QR
                          </Button>
                        </Group>
                      </Paper>

                      {/* Participant Search Input */}
                      <TextInput
                        placeholder='Search participants by name or email'
                        leftSection={<IconSearch size={16} />}
                        rightSection={
                          participantSearchInput && (
                            <ActionIcon
                              size='sm'
                              variant='transparent'
                              onClick={clearParticipantSearch}
                              title='Clear search'
                            >
                              <IconX size={16} />
                            </ActionIcon>
                          )
                        }
                        value={participantSearchInput}
                        onChange={(e) => setParticipantSearchInput(e.target.value)}
                      />

                      <div style={{ position: 'relative', minHeight: 200 }}>
                        <LoadingOverlay visible={isLoadingAllParticipants} />

                        {filteredParticipants.length > 0 ? (
                          <Stack gap='sm'>
                            <Table striped highlightOnHover>
                              <Table.Thead>
                                <Table.Tr>
                                  <Table.Th style={{ width: 50 }}>
                                    <Checkbox
                                      checked={allParticipantsSelected}
                                      indeterminate={
                                        someParticipantsSelected && !allParticipantsSelected
                                      }
                                      onChange={toggleSelectAllParticipants}
                                    />
                                  </Table.Th>
                                  <Table.Th>Name</Table.Th>
                                  <Table.Th>Email</Table.Th>
                                  <Table.Th>User ID</Table.Th>
                                  <Table.Th>
                                    Assigned {statusKurikulum ? 'Courses' : 'Modules'}
                                  </Table.Th>
                                </Table.Tr>
                              </Table.Thead>
                              <Table.Tbody>
                                {filteredParticipants.map((participant: CourseParticipant) => {
                                  const participantId = String(participant.userId);
                                  const isSelected = selectedParticipants.includes(participantId);
                                  const assignedModules =
                                    participantModuleAssignments[participantId] || [];

                                  return (
                                    <Table.Tr key={participant.userId}>
                                      <Table.Td>
                                        <Checkbox
                                          checked={isSelected}
                                          onChange={() => toggleParticipant(participantId)}
                                        />
                                      </Table.Td>
                                      <Table.Td>
                                        <Group>
                                          <Avatar color='#7B2183' radius='xl' size='sm'>
                                            {participant.fullname.charAt(0)}
                                          </Avatar>
                                          <Text fw={500}>{participant.fullname}</Text>
                                        </Group>
                                      </Table.Td>
                                      <Table.Td>
                                        <Text size='sm' c='dimmed'>
                                          {participant.email}
                                        </Text>
                                      </Table.Td>
                                      <Table.Td>
                                        <Text size='sm' c='dimmed'>
                                          {participant.userId}
                                        </Text>
                                      </Table.Td>
                                      <Table.Td>
                                        {isSelected ? (
                                          <Stack gap='xs'>
                                            <Group gap='xs'>
                                              {selectedModules.map((moduleId) => {
                                                const module =
                                                  modules.find(
                                                    (m: any) => m.moduleId === moduleId
                                                  ) ||
                                                  programModulesData.find(
                                                    (m) => m.moduleId === moduleId
                                                  );
                                                const isAssigned =
                                                  assignedModules.includes(moduleId);

                                                return (
                                                  <Checkbox
                                                    key={moduleId}
                                                    size='xs'
                                                    label={
                                                      <Text size='xs'>
                                                        {module?.moduleName || moduleId}
                                                      </Text>
                                                    }
                                                    checked={isAssigned}
                                                    onChange={() =>
                                                      toggleParticipantModule(
                                                        participantId,
                                                        moduleId
                                                      )
                                                    }
                                                  />
                                                );
                                              })}
                                            </Group>
                                            <Text size='xs' c='dimmed'>
                                              {assignedModules.length} of {selectedModules.length}{' '}
                                              assigned
                                            </Text>
                                          </Stack>
                                        ) : (
                                          <Text size='xs' c='dimmed'>
                                            Select participant to assign modules
                                          </Text>
                                        )}
                                      </Table.Td>
                                    </Table.Tr>
                                  );
                                })}
                              </Table.Tbody>
                            </Table>
                          </Stack>
                        ) : (
                          !isLoadingAllParticipants && (
                            <Text ta='center' c='dimmed' py='xl'>
                              {debouncedParticipantSearch
                                ? `No participants found matching "${debouncedParticipantSearch}"`
                                : 'No participants found for the selected courses'}
                            </Text>
                          )
                        )}
                      </div>

                      {/* Participant Pagination */}
                      {participantsTotalPages > 1 && (
                        <Group justify='center'>
                          <Button
                            variant='outline'
                            size='sm'
                            disabled={participantPage === 1}
                            onClick={() => setParticipantPage((p) => p - 1)}
                          >
                            Previous
                          </Button>
                          <Text size='sm'>
                            Page {participantPage} of {participantsTotalPages}
                          </Text>
                          <Button
                            variant='outline'
                            size='sm'
                            disabled={participantPage === participantsTotalPages}
                            onClick={() => setParticipantPage((p) => p + 1)}
                          >
                            Next
                          </Button>
                        </Group>
                      )}
                    </Stack>
                  )}
                </Stack>
              )}

              {participantError && (
                <Alert icon={<IconX size={16} />} color='red'>
                  Failed to load participants. Please try again.
                </Alert>
              )}
            </Stack>
          </Tabs.Panel>
        </Tabs>

        <Group justify='space-between' mt='lg'>
          <Group>
            {activeTab === 'participants' && (
              <Button variant='outline' onClick={backToModules}>
                Back to {statusKurikulum ? 'Courses' : 'Modules'}
              </Button>
            )}
          </Group>

          <Group>
            <Button variant='outline' onClick={cancelAndClose}>
              Cancel
            </Button>

            {activeTab === 'modules' ? (
              <Group>
                <Button
                  variant='outline'
                  onClick={skipParticipantSelection}
                  disabled={selectedModules.length === 0}
                  leftSection={<IconCheck size={16} />}
                >
                  Select Course
                </Button>
              </Group>
            ) : (
              <Button
                onClick={confirmSelection}
                disabled={selectedModules.length === 0}
                leftSection={<IconCheck size={16} />}
              >
                Confirm Selection ({selectedModules.length} {statusKurikulum ? 'course' : 'module'},{' '}
                {selectedParticipants.length} participant
                {selectedParticipants.length === 1 ? '' : 's'})
              </Button>
            )}
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
};

export default ModuleSearchModal;
